import { TranslatedStandard } from "types";
import { RiskMitigation } from "components/Risk/types";

// Every customer has the base machine standard by default — never offer to
// "buy" it. Mirrors the constant in components/inputs/StandardsSelectInput.tsx.
export const BASE_MACHINE_STANDARD = "EN ISO 12100:2010";

// applied_standards are stored as "name:version" (e.g. "EN ISO 13849-1:2015"),
// while grounded_standards[].standard carries the bare name. Strip the trailing
// :<version> so the two can be compared on equal footing.
export const normalizeStandardName = (value: string): string => {
  const lastColon = value.lastIndexOf(":");
  const name = lastColon === -1 ? value : value.slice(0, lastColon);
  return name.trim().toLowerCase();
};

// Aggregate, across every mitigation, the applied standards the AI could NOT
// ground on (present in applied_standards but absent from grounded_standards).
// Returns the deduped original "name:version" display strings.
//
// A null/undefined grounded_standards means nothing was grounded at all — the
// customer owns none of the applied standards, which is exactly the case the
// buy flow exists for. So a missing array is treated as empty (every applied
// standard, minus the always-owned base standard, counts as ungrounded).
export const getUngroundedStandards = (
  mitigations: RiskMitigation[]
): string[] => {
  const baseName = normalizeStandardName(BASE_MACHINE_STANDARD);
  const seen = new Set<string>();
  const result: string[] = [];

  for (const mitigation of mitigations) {
    const grounded = new Set(
      (mitigation.grounded_standards || []).map((g) =>
        normalizeStandardName(g.standard)
      )
    );

    for (const applied of mitigation.applied_standards || []) {
      const name = normalizeStandardName(applied);

      if (name === baseName || grounded.has(name) || seen.has(name)) {
        continue;
      }

      seen.add(name);
      result.push(applied);
    }
  }

  return result;
};

// Aggregate, across every mitigation, the standards the AI WAS able to ground
// on (the customer already has access). Deduped by normalized name; returns the
// stored grounded_standards[].standard values (bare names).
export const getGroundedStandards = (
  mitigations: RiskMitigation[]
): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const mitigation of mitigations) {
    for (const grounded of mitigation.grounded_standards || []) {
      const name = normalizeStandardName(grounded.standard);

      if (seen.has(name)) {
        continue;
      }

      seen.add(name);
      result.push(grounded.standard);
    }
  }

  return result;
};

// Resolve a standard's human-readable title (TranslatedStandard.content) from
// its "name:version" string, matching on the version-stripped name like the
// rest of the app does (e.g. Standards.tsx, StandardSearch).
export const findStandardTitle = (
  translations: TranslatedStandard[] | undefined,
  standard: string
): string | undefined => {
  const target = normalizeStandardName(standard);
  return translations?.find((s) => normalizeStandardName(s.name) === target)
    ?.content;
};
